import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  Globe, Search, TrendingUp, TrendingDown, Minus,
  RefreshCw, Plus, Trash2, ChevronDown,
  AlertCircle, CheckCircle, Bot, MapPin, Image as ImageIcon,
  Video, Newspaper, ShoppingCart, HelpCircle, Star,
  Monitor, Smartphone, X, Eye, ExternalLink, Clock, Pencil, ArrowLeft
} from 'lucide-react';
import axios from 'axios';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine
} from 'recharts';
import config from '../config';
import { nomDePays, trierParNom } from '../utils/libellesInternationaux';

const API_URL = config.API_URL;

/**
 * Pays servis quand l'appel à la liste complète échoue — même liste que le repli du serveur,
 * pour le cas où c'est la requête elle-même qui n'aboutit pas. Les codes sont ceux de
 * DataForSEO : un mot-clé créé depuis ce repli est un mot-clé valide.
 */
const PAYS_DE_REPLI = [
  { code: 2250, name: 'France', countryIsoCode: 'FR' },
  { code: 2056, name: 'Belgium', countryIsoCode: 'BE' },
  { code: 2756, name: 'Switzerland', countryIsoCode: 'CH' },
  { code: 2124, name: 'Canada', countryIsoCode: 'CA' },
  { code: 2840, name: 'United States', countryIsoCode: 'US' },
  { code: 2826, name: 'United Kingdom', countryIsoCode: 'GB' },
  { code: 2276, name: 'Germany', countryIsoCode: 'DE' },
  { code: 2724, name: 'Spain', countryIsoCode: 'ES' },
  { code: 2380, name: 'Italy', countryIsoCode: 'IT' },
  { code: 2620, name: 'Portugal', countryIsoCode: 'PT' },
  { code: 2528, name: 'Netherlands', countryIsoCode: 'NL' },
  { code: 2442, name: 'Luxembourg', countryIsoCode: 'LU' }
];

/** Minutes écoulées depuis la demande de relevé, ou null si la date manque. */
const minutesDAttente = (pendingSince) => {
  if (!pendingSince) return null;
  const minutes = Math.floor((Date.now() - new Date(pendingSince).getTime()) / 60000);
  return Number.isFinite(minutes) && minutes >= 0 ? minutes : null;
};

/**
 * L'attente, datée.
 *
 * « En attente… » sans durée ne dit pas s'il faut patienter ou s'inquiéter — et l'attente
 * réelle va de cinq à dix minutes, entre la file de DataForSEO et le balayage de collecte.
 */
const libelleAttente = (pendingSince) => {
  const minutes = minutesDAttente(pendingSince);
  if (minutes === null) return 'En attente…';
  if (minutes < 1) return 'Demandé à l\'instant';
  return `En attente — ${minutes} min`;
};

/**
 * Pourquoi l'historique est vide — trois causes, trois conduites.
 *
 * Jamais relevé : il faut attendre le prochain passage. Relevés plus anciens que la fenêtre :
 * la collecte s'est arrêtée, il faut aller voir pourquoi. Confondre les deux fait patienter
 * devant une panne — c'est ce qui s'est produit ici, six semaines durant, devant une boîte
 * blanche sans un mot.
 */
const messageHistoriqueVide = (contexte) => {
  if (!contexte || contexte.totalReleves === 0) {
    return 'Aucun relevé pour ce mot-clé. Le premier arrivera au prochain passage planifié, '
      + 'ou tout de suite si vous demandez un relevé depuis la liste.';
  }

  const jours = Math.floor((Date.now() - new Date(contexte.dernierReleveLe).getTime()) / 86400000);
  const date = new Date(contexte.dernierReleveLe).toLocaleDateString('fr-FR');
  return `Aucun relevé sur les ${contexte.fenetreJours} derniers jours. `
    + `Le dernier date du ${date}, il y a ${jours} jours — `
    + `${contexte.totalReleves} relevé${contexte.totalReleves > 1 ? 's' : ''} au total. `
    + 'Un écart de cette ampleur signale un suivi interrompu plutôt qu\'une position stable.';
};

const infobulleAttente = (pendingSince) => {
  const minutes = minutesDAttente(pendingSince);
  const base = 'Relevé demandé auprès de DataForSEO. Le résultat arrive généralement en 5 à 10 minutes, '
    + 'et s\'affiche tout seul.';
  // Au-delà d'une heure la tâche est abandonnée et le mot-clé redevient interrogeable :
  // le dire évite de laisser croire à un blocage définitif.
  if (minutes !== null && minutes > 20) {
    return `${base} Au-delà d'une heure, la demande est abandonnée et le mot-clé redevient interrogeable.`;
  }
  return base;
};

// Icones SERP Features
const SERP_FEATURE_ICONS = {
  hasAiOverview: { icon: Bot, label: 'AI Overview', color: '#8b5cf6' },
  hasFeaturedSnippet: { icon: Star, label: 'Featured Snippet', color: '#f59e0b' },
  hasLocalPack: { icon: MapPin, label: 'Local Pack', color: '#10b981' },
  hasPeopleAlsoAsk: { icon: HelpCircle, label: 'Autres questions posées', color: '#3b82f6' },
  hasImages: { icon: ImageIcon, label: 'Images', color: '#ec4899' },
  hasVideos: { icon: Video, label: 'Vidéos', color: '#ef4444' },
  hasNews: { icon: Newspaper, label: 'Actualités', color: '#6366f1' },
  hasShopping: { icon: ShoppingCart, label: 'Shopping', color: '#14b8a6' }
};

// Fréquences disponibles
const FREQUENCIES = [
  { value: 'DAILY', label: 'Quotidien' },
  { value: 'WEEKLY', label: 'Hebdomadaire' },
  { value: 'BIWEEKLY', label: 'Bi-hebdomadaire' },
  { value: 'MONTHLY', label: 'Mensuel' },
  { value: 'ON_DEMAND', label: 'À la demande' }
];

// Composant Tooltip personnalisé (InfoTooltip pour éviter conflit avec Recharts)
// position: 'top' (default), 'bottom', 'left' (aligné à gauche pour éviter débordement droite)
const InfoTooltip = ({ children, text, position = 'top' }) => {
  const [show, setShow] = useState(false);

  const getTooltipStyle = () => {
    const base = {
      position: 'absolute',
      padding: '6px 10px',
      backgroundColor: '#1f2937',
      color: '#fff',
      fontSize: '12px',
      borderRadius: '6px',
      whiteSpace: 'nowrap',
      zIndex: 1000,
      pointerEvents: 'none',
      textTransform: 'none'
    };

    switch (position) {
      case 'bottom':
        return { ...base, top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: '6px' };
      case 'left':
        return { ...base, bottom: '100%', right: '0', marginBottom: '6px' };
      default: // top
        return { ...base, bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: '6px' };
    }
  };

  const getArrowStyle = () => {
    const base = { position: 'absolute', borderWidth: '5px', borderStyle: 'solid' };

    switch (position) {
      case 'bottom':
        return { ...base, bottom: '100%', left: '50%', transform: 'translateX(-50%)', borderColor: 'transparent transparent #1f2937 transparent' };
      case 'left':
        return { ...base, top: '100%', right: '10px', borderColor: '#1f2937 transparent transparent transparent' };
      default: // top
        return { ...base, top: '100%', left: '50%', transform: 'translateX(-50%)', borderColor: '#1f2937 transparent transparent transparent' };
    }
  };

  return (
    <div
      style={{ position: 'relative', display: 'inline-flex' }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      {show && (
        <div style={getTooltipStyle()}>
          {text}
          <div style={getArrowStyle()} />
        </div>
      )}
    </div>
  );
};

// Styles communs
const styles = {
  card: {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
  },
  button: {
    padding: '10px 16px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  buttonPrimary: {
    background: '#3b82f6',
    color: 'white'
  },
  buttonSuccess: {
    background: '#10b981',
    color: 'white'
  },
  buttonSecondary: {
    background: '#f3f4f6',
    color: '#374151'
  },
  select: {
    padding: '10px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    background: 'white',
    minWidth: '200px'
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px'
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '6px'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '24px',
    width: '90%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflowY: 'auto',
    overflowX: 'hidden'
  }
};

const SerpTrackingTool = ({ token, selectedClientId }) => {
  // État - Clients (liste servant à associer un projet à un client)
  const [clients, setClients] = useState([]);

  // État - Projets
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [overview, setOverview] = useState(null);
  const [locations, setLocations] = useState([]);
  // Renseigné quand le serveur n'a pu servir que les pays de repli : la modale le dit, plutôt
  // que de laisser croire à une liste complète.
  const [locationsDegrade, setLocationsDegrade] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modales
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [showAddKeywordsModal, setShowAddKeywordsModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedKeyword, setSelectedKeyword] = useState(null);
  const [keywordHistory, setKeywordHistory] = useState([]);
  // Contexte de la fenêtre : sans lui, un historique vide reste muet sur sa cause.
  const [contexteHistorique, setContexteHistorique] = useState(null);

  // Formulaires
  const [newProject, setNewProject] = useState({ name: '', domain: '', clientId: '' });
  const [projetAEditer, setProjetAEditer] = useState(null);
  const [newKeywords, setNewKeywords] = useState({
    keywords: '',
    locationCode: 2250,
    locationName: 'France',
    languageCode: 'fr',
    device: 'DESKTOP',
    frequency: 'WEEKLY'
  });

  // Actions
  const [checkingKeyword, setCheckingKeyword] = useState(null);

  // Headers auth
  const headers = { 'Authorization': `Bearer ${token}` };

  // Charger les projets (filtrés par le client sélectionné dans le header)
  const loadProjects = useCallback(async () => {
    try {
      setLoading(true);
      const params = selectedClientId ? { client: selectedClientId } : {};
      const response = await axios.get(`${API_URL}/api/tools/serp-tracking/projects`, { headers, params });
      const projectsList = response.data.data || [];
      setProjects(projectsList);
      // On garde le projet ouvert s'il survit au filtre client, sinon on revient à la liste.
      // Ouvrir d'office le premier projet masquait l'existence des autres — c'est ce qui
      // distinguait cet outil d'AI Visibility, qui présente ses projets avant d'entrer.
      setSelectedProject(prev => (
        prev && projectsList.some(p => p.id === prev.id) ? prev : null
      ));
    } catch (err) {
      console.error('Erreur chargement projets:', err);
      setError('Impossible de charger les projets');
    } finally {
      setLoading(false);
    }
  }, [token, selectedClientId]);

  // Charger les locations
  const loadLocations = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tools/serp-tracking/locations`, { headers });
      setLocations(response.data.data || []);
      setLocationsDegrade(response.data.degrade || null);
    } catch (err) {
      // Un échec réseau laissait le `<select>` Pays vide, sans que rien ne l'explique à l'écran :
      // on servait alors une modale impossible à valider. Les pays de repli permettent au moins
      // de créer un mot-clé, et le message dit pourquoi la liste est courte.
      console.error('Erreur chargement locations:', err);
      setLocations(PAYS_DE_REPLI);
      setLocationsDegrade('Liste des pays indisponible : seuls les principaux pays sont proposés.');
    }
  }, [token]);

  // Charger les clients (pour associer les projets)
  const loadClients = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/client-management/clients`, { headers });
      setClients(response.data || []);
    } catch (err) {
      console.error('Erreur chargement clients:', err);
    }
  }, [token]);

  // Charger l'overview d'un projet
  const loadOverview = useCallback(async (projectId) => {
    if (!projectId) return;
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/tools/serp-tracking/projects/${projectId}/overview`, { headers });
      setOverview(response.data.data);
    } catch (err) {
      console.error('Erreur chargement overview:', err);
      setError('Impossible de charger les données du projet');
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Charger l'historique d'un keyword
  const loadKeywordHistory = async (keywordId) => {
    try {
      const response = await axios.get(`${API_URL}/api/tools/serp-tracking/keywords/${keywordId}/history?days=30`, { headers });
      const d = response.data.data || {};
      setKeywordHistory(d.history || []);
      setContexteHistorique({
        fenetreJours: d.fenetreJours ?? 30,
        totalReleves: d.totalReleves ?? 0,
        dernierReleveLe: d.dernierReleveLe ?? null
      });
    } catch (err) {
      console.error('Erreur chargement historique:', err);
      setKeywordHistory([]);
      setContexteHistorique(null);
    }
  };

  // Créer un projet
  const createProject = async () => {
    if (!newProject.name || !newProject.domain) return;
    try {
      const response = await axios.post(`${API_URL}/api/tools/serp-tracking/projects`, {
        name: newProject.name,
        domain: newProject.domain,
        clientId: newProject.clientId || null
      }, { headers });
      setProjects([response.data.data, ...projects]);
      setSelectedProject(response.data.data);
      setShowNewProjectModal(false);
      setNewProject({ name: '', domain: '', clientId: '' });
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur création projet');
    }
  };

  /**
   * Relever tous les mots-clés du projet.
   *
   * Ce bouton relisait la base sans rien relever, alors que son icône promettait le
   * contraire. Il déclenche maintenant de vraies soumissions — d'où la confirmation : le
   * geste engage un coût, modeste mais réel, et multiplié par le nombre de mots-clés.
   */
  const [releveEnCours, setReleveEnCours] = useState(false);

  const releverTout = async () => {
    if (!selectedProject) return;
    const nombre = overview?.keywords?.length || 0;
    if (nombre === 0) return;
    if (!window.confirm(
      `Relever maintenant les ${nombre} mot${nombre > 1 ? 's' : ''}-clé${nombre > 1 ? 's' : ''} de ce projet ?`
      + '\n\nLes résultats arrivent en quelques minutes et s\'affichent tout seuls.'
    )) return;

    setReleveEnCours(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/tools/serp-tracking/projects/${selectedProject.id}/check`, {}, { headers }
      );
      const d = data.data || {};
      setSuccessMessage(d.erreurs > 0
        ? `${d.demandes - d.erreurs}/${d.demandes} relevés demandés — ${d.erreurs} en échec.`
        : `${d.demandes} relevé${d.demandes > 1 ? 's' : ''} demandé${d.demandes > 1 ? 's' : ''}. Les résultats arrivent d'ici quelques minutes.`);
      setTimeout(() => setSuccessMessage(null), 10000);
      loadOverview(selectedProject.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors du relevé');
    } finally {
      setReleveEnCours(false);
    }
  };

  // Enregistrer les modifications d'un projet
  const enregistrerProjet = async () => {
    if (!projetAEditer?.name?.trim() || !projetAEditer?.domain?.trim()) return;
    try {
      await axios.put(
        `${API_URL}/api/tools/serp-tracking/projects/${projetAEditer.id}`,
        {
          name: projetAEditer.name.trim(),
          domain: projetAEditer.domain.trim(),
          clientId: projetAEditer.clientId || null
        },
        { headers }
      );
      setProjetAEditer(null);
      loadProjects();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur modification projet');
    }
  };

  // Supprimer un projet
  const deleteProject = async (projectId) => {
    const projet = projects.find(p => p.id === projectId);
    // La confirmation **nomme** le projet : depuis une grille de cartes, « Supprimer ce
    // projet ? » ne dit pas lequel. Et elle décrit ce qui se passe vraiment — le serveur
    // détruit le projet, ses mots-clés et leur historique. L'annoncer permet d'hésiter à bon
    // escient, là où promettre une archive qu'aucun écran ne sait rouvrir trompe deux fois.
    const nombre = projet?.keywordsCount || 0;
    const detail = nombre > 0
      ? ` Ses ${nombre} mot${nombre > 1 ? 's' : ''}-clé${nombre > 1 ? 's' : ''} et leurs relevés seront supprimés.`
      : '';
    if (!window.confirm(
      `Supprimer « ${projet?.name || 'ce projet'} » ?${detail}`
      + '\n\nCette suppression est définitive : l\'historique des positions ne sera pas récupérable.'
    )) return;

    try {
      await axios.delete(`${API_URL}/api/tools/serp-tracking/projects/${projectId}`, { headers });
      setProjects(projects.filter(p => p.id !== projectId));
      // On revient à la liste plutôt que d'ouvrir un autre projet : après une suppression,
      // se retrouver dans un projet qu'on n'a pas choisi est déroutant.
      if (selectedProject?.id === projectId) setSelectedProject(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur suppression projet');
    }
  };

  // Ajouter des mots-clés
  const addKeywords = async () => {
    const keywordsList = newKeywords.keywords
      .split('\n')
      .map(k => k.trim())
      .filter(k => k.length > 0);

    if (keywordsList.length === 0) return;

    try {
      const payload = {
        keywords: keywordsList.map(keyword => ({
          keyword,
          locationCode: newKeywords.locationCode,
          locationName: newKeywords.locationName,
          languageCode: newKeywords.languageCode,
          device: newKeywords.device,
          frequency: newKeywords.frequency
        }))
      };

      await axios.post(`${API_URL}/api/tools/serp-tracking/projects/${selectedProject.id}/keywords`, payload, { headers });
      setShowAddKeywordsModal(false);
      setNewKeywords({ ...newKeywords, keywords: '' });
      loadOverview(selectedProject.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur ajout keywords');
    }
  };

  // Supprimer un keyword
  const deleteKeyword = async (keywordId) => {
    try {
      await axios.delete(`${API_URL}/api/tools/serp-tracking/keywords/${keywordId}`, { headers });
      loadOverview(selectedProject.id);
    } catch (err) {
      setError('Erreur suppression keyword');
    }
  };

  // Message de succès temporaire
  const [successMessage, setSuccessMessage] = useState(null);

  // Vérifier un keyword manuellement
  const checkKeyword = async (keywordId) => {
    setCheckingKeyword(keywordId);
    try {
      const response = await axios.post(`${API_URL}/api/tools/serp-tracking/keywords/${keywordId}/check`, {}, { headers });

      // Si statut pending (Standard Queue), afficher message de confirmation
      if (response.data.data?.status === 'pending') {
        setSuccessMessage(response.data.data.message
          || 'Relevé demandé. Le résultat arrive généralement en 5 à 10 minutes.');
        setTimeout(() => setSuccessMessage(null), 10000);
      }

      loadOverview(selectedProject.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur vérification position');
    } finally {
      setCheckingKeyword(null);
    }
  };

  // Chargement initial + rechargement à chaque changement de client dans le header
  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  // Données de référence, indépendantes du client sélectionné
  useEffect(() => {
    loadLocations();
    loadClients();
  }, [loadLocations, loadClients]);

  // Charger l'overview quand le projet change
  useEffect(() => {
    if (selectedProject?.id) {
      loadOverview(selectedProject.id);
    }
  }, [selectedProject?.id]);

  /**
   * Faire avancer le compteur d'attente.
   *
   * Le sondage ci-dessous ne redessine l'écran qu'en cas de résultat récupéré : sans ce
   * battement, « En attente — 3 min » resterait figé à la valeur du premier rendu, et un
   * compteur qui ne compte pas vaut moins qu'un libellé sans chiffre.
   */
  const [, battement] = useState(0);
  useEffect(() => {
    if (!overview?.keywords?.some(kw => kw.status === 'PENDING')) return undefined;
    const timer = setInterval(() => battement(n => n + 1), 30000);
    return () => clearInterval(timer);
  }, [overview]);

  // Auto-refresh si des keywords sont en PENDING (polling toutes les 30s)
  useEffect(() => {
    const hasPendingKeywords = overview?.keywords?.some(kw => kw.status === 'PENDING');

    if (!hasPendingKeywords || !selectedProject?.id) return;

    const fetchPendingResults = async () => {
      try {
        const response = await axios.post(`${API_URL}/api/tools/serp-tracking/fetch-pending-results`, {}, { headers });
        if (response.data.data?.updated > 0) {
          // Rafraîchir l'overview si des résultats ont été récupérés
          loadOverview(selectedProject.id);
        }
      } catch (err) {
        console.error('Erreur polling résultats:', err);
      }
    };

    // Premier appel après 30s, puis toutes les 30s
    const interval = setInterval(fetchPendingResults, 30000);

    // Cleanup
    return () => clearInterval(interval);
  }, [overview?.keywords, selectedProject?.id]);

  // Rendu du changement de position
  const renderPositionChange = (change) => {
    if (change === null || change === undefined) return <Minus size={14} style={{ color: '#9ca3af' }} />;
    if (change > 0) return <span style={{ display: 'flex', alignItems: 'center', color: '#10b981' }}><TrendingUp size={14} style={{ marginRight: '4px' }} />+{change}</span>;
    if (change < 0) return <span style={{ display: 'flex', alignItems: 'center', color: '#ef4444' }}><TrendingDown size={14} style={{ marginRight: '4px' }} />{change}</span>;
    return <Minus size={14} style={{ color: '#9ca3af' }} />;
  };

  // Rendu des SERP features avec tooltips
  const renderSerpFeatures = (features) => {
    if (!features) return null;
    const activeFeatures = Object.entries(SERP_FEATURE_ICONS).filter(([key]) => features[key]);
    if (activeFeatures.length === 0) return <span style={{ color: '#d1d5db' }}>-</span>;

    return (
      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
        {activeFeatures.map(([key, { icon: Icon, label, color }]) => (
          <InfoTooltip key={key} text={label}>
            <div
              style={{
                padding: '4px',
                borderRadius: '4px',
                backgroundColor: `${color}20`,
                cursor: 'help'
              }}
            >
              <Icon size={14} style={{ color }} />
            </div>
          </InfoTooltip>
        ))}
      </div>
    );
  };

  return (
    <div style={{ width: '100%', padding: '0' }}>
      {/* En-tête */}
      <div style={styles.card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
          {/* Un seul bandeau, qui change de rôle : présentation de l'outil sur les cartes,
              identité du projet une fois dedans. La flèche est le seul retour vers la liste
              depuis que le sélecteur a disparu — sans elle, on serait prisonnier du projet. */}
          {selectedProject ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
              <button
                onClick={() => { setSelectedProject(null); setOverview(null); }}
                title="Tous les projets"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', display: 'flex', padding: '4px' }}
              >
                <ArrowLeft size={20} />
              </button>
              <div style={{ minWidth: 0 }}>
                <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', margin: 0, display: 'flex', alignItems: 'center' }}>
                  <Globe style={{ marginRight: '8px', height: '20px', width: '20px', color: '#3b82f6' }} />
                  {selectedProject.name}
                </h2>
                <p style={{ color: '#6b7280', margin: '2px 0 0', fontSize: '13px' }}>
                  {selectedProject.domain}{selectedProject.clientName ? ` · ${selectedProject.clientName}` : ''}
                </p>
              </div>
            </div>
          ) : (
            <div>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '600',
                color: '#111827',
                display: 'flex',
                alignItems: 'center',
                margin: '0 0 8px 0'
              }}>
                <Globe style={{ marginRight: '8px', height: '24px', width: '24px', color: '#3b82f6' }} />
                SERP Tracking
              </h2>
              <p style={{ color: '#6b7280', margin: '0', fontSize: '14px' }}>
                Suivez vos positions SEO et détectez les AI Overviews (GEO)
              </p>
            </div>
          )}

          {/* Les actions du projet occupent la place du bouton de création : une seule
              rangée de commandes, et la carte du sélecteur disparaît entièrement. */}
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {selectedProject ? (
              <>
                <button
                  onClick={() => setShowAddKeywordsModal(true)}
                  style={{ ...styles.button, ...styles.buttonSuccess }}
                >
                  <Plus size={18} />
                  Ajouter mots-clés
                </button>
                <button
                  onClick={releverTout}
                  disabled={releveEnCours || !(overview?.keywords?.length)}
                  style={{
                    ...styles.button, ...styles.buttonSecondary,
                    opacity: (releveEnCours || !(overview?.keywords?.length)) ? 0.5 : 1
                  }}
                  title="Relever la position de tous les mots-clés maintenant"
                >
                  <RefreshCw size={18} style={{ animation: releveEnCours ? 'spin 1s linear infinite' : 'none' }} />
                  Relever maintenant
                </button>
              </>
            ) : (
              <button
                onClick={() => setShowNewProjectModal(true)}
                style={{ ...styles.button, ...styles.buttonPrimary }}
              >
                <Plus size={18} />
                Nouveau projet
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Message de succès */}
      {successMessage && (
        <div style={{
          ...styles.card,
          background: '#f0fdf4',
          border: '1px solid #bbf7d0',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          color: '#16a34a'
        }}>
          <CheckCircle size={18} />
          {successMessage}
          <button
            onClick={() => setSuccessMessage(null)}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#16a34a' }}
          >
            <X size={18} />
          </button>
        </div>
      )}

      {/* Erreur */}
      {error && (
        <div style={{
          ...styles.card,
          background: '#fef2f2',
          border: '1px solid #fecaca',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          color: '#dc2626'
        }}>
          <AlertCircle size={18} />
          {error}
          <button
            onClick={() => setError(null)}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626' }}
          >
            <X size={18} />
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ ...styles.card, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '256px' }}>
          <RefreshCw size={32} style={{ color: '#3b82f6', animation: 'spin 1s linear infinite' }} />
          <style>{`
            @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
            @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
          `}</style>
        </div>
      ) : !selectedProject ? (
        projects.length === 0 ? (
          <div style={{ ...styles.card, textAlign: 'center', padding: '48px' }}>
            <Globe size={48} style={{ color: '#d1d5db', marginBottom: '16px' }} />
            <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', margin: '0 0 8px 0' }}>
              Aucun projet
            </h3>
            <p style={{ color: '#6b7280', margin: '0 0 24px 0' }}>
              Créez votre premier projet pour commencer à suivre vos positions.
            </p>
            <button
              onClick={() => setShowNewProjectModal(true)}
              style={{ ...styles.button, ...styles.buttonPrimary, margin: '0 auto' }}
            >
              Créer un projet
            </button>
          </div>
        ) : (
          /* Les projets avant d'entrer dans l'un d'eux, comme AI Visibility. Ouvrir d'office
             le premier masquait l'existence des autres. */
          <div style={{ display: 'grid', gap: '16px', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
            {projects.map(project => (
              <div
                key={project.id}
                style={{ ...styles.card, cursor: 'pointer', transition: 'transform 0.2s' }}
                onClick={() => setSelectedProject(project)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                  <div style={{ minWidth: 0 }}>
                    <h3 style={{ margin: '0 0 4px', fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                      {project.name}
                    </h3>
                    <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>{project.domain}</p>
                    {project.clientName && (
                      <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#9ca3af' }}>
                        Client : {project.clientName}
                      </p>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                    <div style={{
                      background: '#f3f4f6', padding: '4px 8px', borderRadius: '6px',
                      fontSize: '12px', color: '#374151', whiteSpace: 'nowrap'
                    }}>
                      {project.keywordsCount || 0} mot{(project.keywordsCount || 0) > 1 ? 's' : ''}-clé{(project.keywordsCount || 0) > 1 ? 's' : ''}
                    </div>
                    <InfoTooltip text="modifier ce projet" position="left">
                      <button
                        onClick={(e) => {
                          // Sans quoi le clic ouvrirait aussi le projet derrière la modale.
                          e.stopPropagation();
                          setProjetAEditer({
                            id: project.id,
                            name: project.name,
                            domain: project.domain,
                            clientId: project.clientId || ''
                          });
                        }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#9ca3af', display: 'flex' }}
                      >
                        <Pencil size={16} />
                      </button>
                    </InfoTooltip>
                    <InfoTooltip text="supprimer ce projet" position="left">
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteProject(project.id); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#ef4444', display: 'flex' }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </InfoTooltip>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      ) : overview ? (
        <>
          {/* Stats Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
            <div style={styles.card}>
              <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Mots-clés</div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#111827' }}>{overview.stats.totalKeywords}</div>
            </div>
            <div style={styles.card}>
              <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Position moyenne</div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#111827' }}>{overview.stats.avgPosition || '-'}</div>
            </div>
            <div style={styles.card}>
              <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Gains / Pertes</div>
              <div style={{ fontSize: '28px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: '#10b981' }}>+{overview.stats.positionGains}</span>
                <span style={{ color: '#d1d5db' }}>/</span>
                <span style={{ color: '#ef4444' }}>-{overview.stats.positionLosses}</span>
              </div>
            </div>
            <div style={styles.card}>
              <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>AI Overviews</div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#111827', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Bot size={24} style={{ color: '#8b5cf6' }} />
                {overview.stats.withAiOverview}
                <span style={{ fontSize: '14px', color: '#9ca3af', fontWeight: '400' }}>
                  ({overview.stats.mentionedInAi} mentions)
                </span>
              </div>
            </div>
          </div>

          {/* Tableau des mots-clés */}
          <div style={{ ...styles.card, padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>Mot-clé</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>Pays</th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>Appareil</th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>
                      <InfoTooltip text="position organique dans Google (SEO)" position="bottom">
                        <span style={{ cursor: 'help' }}>Position</span>
                      </InfoTooltip>
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>
                      <InfoTooltip text="évolution vs dernière vérification" position="bottom">
                        <span style={{ cursor: 'help' }}>Évolution</span>
                      </InfoTooltip>
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>
                      <InfoTooltip text="AI Overview (GEO) - résumé IA de Google" position="bottom">
                        <span style={{ cursor: 'help' }}>AI Overview</span>
                      </InfoTooltip>
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>
                      <InfoTooltip text="SERP features détectés (Local Pack, Images, PAA...)" position="bottom">
                        <span style={{ cursor: 'help' }}>Enrichis</span>
                      </InfoTooltip>
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {overview.keywords.map((kw) => (
                    <tr key={kw.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: '500', color: '#111827' }}>{kw.keyword}</div>
                        {/*
                          La page que Google a classée pour ce mot-clé, au dernier relevé. Elle
                          est tronquée faute de place, mais le `title` rend l'adresse entière au
                          survol et le lien l'ouvre : la donner à lire sans permettre d'aller la
                          voir, c'est montrer une piste sans le chemin. `nofollow noopener` parce
                          qu'on ouvre là une page dont on ne maîtrise pas le contenu.
                        */}
                        {kw.url && (
                          <a
                            href={kw.url}
                            target="_blank"
                            rel="nofollow noopener noreferrer"
                            title={kw.url}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              display: 'block',
                              fontSize: '12px',
                              color: '#9ca3af',
                              maxWidth: '250px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              textDecoration: 'none'
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = '#2563eb'; e.currentTarget.style.textDecoration = 'underline'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = '#9ca3af'; e.currentTarget.style.textDecoration = 'none'; }}
                          >
                            {kw.url}
                          </a>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '14px', color: '#6b7280' }}>{kw.location}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        {kw.device === 'DESKTOP' ? (
                          <Monitor size={16} style={{ color: '#9ca3af' }} />
                        ) : (
                          <Smartphone size={16} style={{ color: '#9ca3af' }} />
                        )}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        {kw.status === 'PENDING' ? (
                          <InfoTooltip text={infobulleAttente(kw.pendingSince)}>
                            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', color: '#f59e0b', cursor: 'help' }}>
                              <Clock size={14} style={{ animation: 'pulse 2s infinite' }} />
                              <span style={{ fontSize: '12px' }}>{libelleAttente(kw.pendingSince)}</span>
                            </span>
                          </InfoTooltip>
                        ) : kw.position ? (
                          <span style={{
                            fontWeight: '700',
                            color: kw.position <= 3 ? '#10b981' : kw.position <= 10 ? '#3b82f6' : '#6b7280'
                          }}>
                            #{kw.position}
                          </span>
                        ) : kw.lastCheckedAt ? (
                          <InfoTooltip text="Non trouvé dans le top 100">
                            <span style={{ color: '#9ca3af', cursor: 'help' }}>100+</span>
                          </InfoTooltip>
                        ) : (
                          <InfoTooltip text="Position non encore vérifiée">
                            <span style={{ color: '#d1d5db', cursor: 'help' }}>-</span>
                          </InfoTooltip>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        {renderPositionChange(kw.positionChange)}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        {kw.hasAiOverview ? (
                          <InfoTooltip text={kw.aiOverviewMentioned ? "AI Overview présent - Votre site est cité !" : "AI Overview présent sur cette SERP"}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', cursor: 'help' }}>
                              <Bot size={16} style={{ color: '#8b5cf6' }} />
                              {kw.aiOverviewMentioned && (
                                <CheckCircle size={12} style={{ color: '#10b981' }} />
                              )}
                            </div>
                          </InfoTooltip>
                        ) : (
                          <InfoTooltip text="Pas d'AI Overview sur cette SERP">
                            <span style={{ color: '#d1d5db', cursor: 'help' }}>-</span>
                          </InfoTooltip>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        {renderSerpFeatures(kw.serpFeatures)}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                          <InfoTooltip text="voir la SERP sur Google" position="left">
                            <a
                              href={`https://www.google.com/search?q=${encodeURIComponent(kw.keyword)}&hl=${kw.languageCode || 'fr'}&gl=${kw.location?.substring(0, 2)?.toLowerCase() || 'fr'}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#9ca3af', display: 'flex' }}
                            >
                              <ExternalLink size={16} />
                            </a>
                          </InfoTooltip>
                          <InfoTooltip text="voir l'historique des positions" position="left">
                            <button
                              onClick={() => {
                                setSelectedKeyword(kw);
                                loadKeywordHistory(kw.id);
                                setShowHistoryModal(true);
                              }}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#9ca3af' }}
                            >
                              <Eye size={16} />
                            </button>
                          </InfoTooltip>
                          <InfoTooltip text="relever la position (résultat en 5 à 10 minutes)" position="left">
                            <button
                              onClick={() => checkKeyword(kw.id)}
                              disabled={checkingKeyword === kw.id || kw.status === 'PENDING'}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#9ca3af', opacity: (checkingKeyword === kw.id || kw.status === 'PENDING') ? 0.5 : 1 }}
                            >
                              <RefreshCw size={16} style={{ animation: checkingKeyword === kw.id ? 'spin 1s linear infinite' : 'none' }} />
                            </button>
                          </InfoTooltip>
                          <InfoTooltip text="supprimer ce mot-clé" position="left">
                            <button
                              onClick={() => deleteKeyword(kw.id)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#9ca3af' }}
                            >
                              <Trash2 size={16} />
                            </button>
                          </InfoTooltip>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {overview.keywords.length === 0 && (
                <div style={{ padding: '48px', textAlign: 'center' }}>
                  <Search size={32} style={{ color: '#d1d5db', marginBottom: '8px' }} />
                  <p style={{ color: '#6b7280', margin: '0 0 8px 0' }}>Aucun mot-clé ajouté</p>
                  <button
                    onClick={() => setShowAddKeywordsModal(true)}
                    style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    Ajouter des mots-clés
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      ) : null}

      {/* Modal: Nouveau projet */}
      {/* Modification d'un projet, depuis sa carte. Le rattachement client se règle ici et
          à la création — jamais dans la barre du projet, où il passait pour un filtre. */}
      {projetAEditer && ReactDOM.createPortal(
        <div style={styles.modalOverlay} onClick={() => setProjetAEditer(null)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>
                Modifier le projet
              </h2>
              <button
                onClick={() => setProjetAEditer(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                Nom du projet
              </label>
              <input
                type="text"
                value={projetAEditer.name}
                onChange={e => setProjetAEditer({ ...projetAEditer, name: e.target.value })}
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                Domaine à suivre
              </label>
              <input
                type="text"
                value={projetAEditer.domain}
                onChange={e => setProjetAEditer({ ...projetAEditer, domain: e.target.value })}
                placeholder="exemple.fr"
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' }}
              />
              <p style={{ fontSize: '12px', color: '#6b7280', margin: '6px 0 0 0' }}>
                C'est ce domaine qu'on cherche dans les résultats. Le changer ne réécrit pas
                l'historique déjà relevé.
              </p>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                Client (optionnel)
              </label>
              <select
                value={projetAEditer.clientId}
                onChange={e => setProjetAEditer({ ...projetAEditer, clientId: e.target.value })}
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', boxSizing: 'border-box', backgroundColor: '#fff' }}
              >
                <option value="">Aucun client</option>
                {/* Triés : une liste dans l'ordre de création oblige à la parcourir en
                    entier pour retrouver un client. */}
                {trierParNom(clients).map(client => (
                  <option key={client.id} value={client.id}>{client.name}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button onClick={() => setProjetAEditer(null)} style={{ ...styles.button, ...styles.buttonSecondary }}>
                Annuler
              </button>
              <button
                onClick={enregistrerProjet}
                disabled={!projetAEditer.name?.trim() || !projetAEditer.domain?.trim()}
                style={{
                  ...styles.button, ...styles.buttonPrimary,
                  opacity: (!projetAEditer.name?.trim() || !projetAEditer.domain?.trim()) ? 0.5 : 1
                }}
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showNewProjectModal && ReactDOM.createPortal(
        <div style={styles.modalOverlay} onClick={() => setShowNewProjectModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>
                Nouveau projet
              </h2>
              <button
                onClick={() => setShowNewProjectModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                Nom du projet
              </label>
              <input
                type="text"
                value={newProject.name}
                onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                placeholder="Mon site e-commerce"
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                Domaine à tracker
              </label>
              <input
                type="text"
                value={newProject.domain}
                onChange={(e) => setNewProject({ ...newProject, domain: e.target.value })}
                placeholder="example.com"
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', boxSizing: 'border-box' }}
              />
              <p style={{ fontSize: '12px', color: '#6b7280', margin: '6px 0 0 0' }}>Sans http:// ni www.</p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                Client (optionnel)
              </label>
              <select
                value={newProject.clientId}
                onChange={(e) => setNewProject({ ...newProject, clientId: e.target.value })}
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', boxSizing: 'border-box', backgroundColor: '#fff' }}
              >
                <option value="">Aucun client</option>
                {/* Triés : une liste dans l'ordre de création oblige à la parcourir en
                    entier pour retrouver un client. */}
                {trierParNom(clients).map(client => (
                  <option key={client.id} value={client.id}>{client.name}</option>
                ))}
              </select>
              <p style={{ fontSize: '12px', color: '#6b7280', margin: '6px 0 0 0' }}>Pour classer ce projet dans un dossier client</p>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={() => setShowNewProjectModal(false)}
                style={{ padding: '10px 16px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: '500', color: '#374151' }}
              >
                Annuler
              </button>
              <button
                onClick={createProject}
                disabled={!newProject.name || !newProject.domain}
                style={{ padding: '10px 16px', borderRadius: '8px', border: 'none', background: '#3b82f6', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: '500', opacity: (!newProject.name || !newProject.domain) ? 0.5 : 1 }}
              >
                Créer
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal: Ajouter keywords */}
      {showAddKeywordsModal && ReactDOM.createPortal(
        <div style={styles.modalOverlay} onClick={() => setShowAddKeywordsModal(false)}>
          <div style={{ ...styles.modalContent, maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>
                Ajouter des mots-clés
              </h2>
              <button
                onClick={() => setShowAddKeywordsModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                Mots-clés (un par ligne)
              </label>
              <textarea
                value={newKeywords.keywords}
                onChange={(e) => setNewKeywords({ ...newKeywords, keywords: e.target.value })}
                placeholder={"mot clé 1\nmot clé 2\nmot clé 3"}
                rows={5}
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Pays
                </label>
                <select
                  value={newKeywords.locationCode}
                  onChange={(e) => {
                    const loc = locations.find(l => l.code === parseInt(e.target.value));
                    setNewKeywords({
                      ...newKeywords,
                      locationCode: parseInt(e.target.value),
                      locationName: loc?.name || 'France'
                    });
                  }}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', background: '#fff', boxSizing: 'border-box' }}
                >
                  {/* DataForSEO rend « Belgium », « Germany »… au milieu d'une interface
                      française : on traduit, en gardant son libellé si le code est inconnu. */}
                  {trierParNom(locations, l => nomDePays(l.countryIsoCode, l.name)).map(loc => (
                    <option key={loc.code} value={loc.code}>{nomDePays(loc.countryIsoCode, loc.name)}</option>
                  ))}
                </select>
                {locationsDegrade && (
                  <div style={{ marginTop: '6px', fontSize: '12px', color: '#b45309', display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                    <AlertCircle size={13} style={{ flexShrink: 0, marginTop: '1px' }} />
                    <span>{locationsDegrade}</span>
                  </div>
                )}
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                  Appareil
                </label>
                <select
                  value={newKeywords.device}
                  onChange={(e) => setNewKeywords({ ...newKeywords, device: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', background: '#fff', boxSizing: 'border-box' }}
                >
                  <option value="DESKTOP">Desktop</option>
                  <option value="MOBILE">Mobile</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                Fréquence de suivi
              </label>
              <select
                value={newKeywords.frequency}
                onChange={(e) => setNewKeywords({ ...newKeywords, frequency: e.target.value })}
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', background: '#fff', boxSizing: 'border-box' }}
              >
                {FREQUENCIES.map(f => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={() => setShowAddKeywordsModal(false)}
                style={{ padding: '10px 16px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: '500', color: '#374151' }}
              >
                Annuler
              </button>
              <button
                onClick={addKeywords}
                disabled={!newKeywords.keywords.trim()}
                style={{ padding: '10px 16px', borderRadius: '8px', border: 'none', background: '#10b981', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: '500', opacity: !newKeywords.keywords.trim() ? 0.5 : 1 }}
              >
                Ajouter
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal: Historique */}
      {showHistoryModal && selectedKeyword && ReactDOM.createPortal(
        <div style={styles.modalOverlay} onClick={() => setShowHistoryModal(false)}>
          <div style={{ ...styles.modalContent, maxWidth: '700px' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>
                Historique: {selectedKeyword.keyword}
              </h2>
              <button
                onClick={() => setShowHistoryModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}
              >
                <X size={20} />
              </button>
            </div>

            {keywordHistory.length === 0 ? (
              <div style={{
                padding: '32px 24px', textAlign: 'center', color: '#6b7280',
                background: '#f9fafb', borderRadius: '8px', marginBottom: '24px', lineHeight: 1.6
              }}>
                {messageHistoriqueVide(contexteHistorique)}
              </div>
            ) : (
            <>
            <div style={{ height: '256px', marginBottom: '24px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={keywordHistory}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis reversed domain={[1, 'auto']} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <ReferenceLine y={10} stroke="#ccc" strokeDasharray="3 3" label="Top 10" />
                  <Line
                    type="monotone"
                    dataKey="position"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ background: '#f9fafb', position: 'sticky', top: 0 }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '500' }}>Date</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: '500' }}>Position</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: '500' }}>Évolution</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: '500' }}>AI Overview</th>
                  </tr>
                </thead>
                <tbody>
                  {keywordHistory.map((h, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px 12px' }}>{h.date}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'center', fontWeight: '500' }}>#{h.position || '-'}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'center' }}>{renderPositionChange(h.change)}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                        {h.hasAiOverview ? (
                          <Bot size={14} style={{ color: '#8b5cf6' }} />
                        ) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SerpTrackingTool;
